import {useRequest} from '@aboutyou/data-fetcher';
import {
  useIsBrandPage,
  useIsCategoryPage,
  useIsPremiumPage,
  useIsSearchResultsPage,
  useIsWishlistPage,
  usePathname,
} from '@aboutyou/router';
import {startTransition, useEffect, useRef, useState} from 'react';

const SHOULD_ANIMATE_FIRST_IMAGE_GALLERY = 'shouldAnimateFirstImageGallery';

export const useShouldAnimateFirstImageGallery = () => {
  const isCategoryPage = useIsCategoryPage();
  const isPremiumPage = useIsPremiumPage();
  const isBrandPage = useIsBrandPage();
  const isWishlist = useIsWishlistPage();
  const isSRP = useIsSearchResultsPage();

  const pathname = usePathname();
  const {data, updateCache} = useRequest<{
    categoryPage: boolean;
    brandPage: boolean;
    wishlist: boolean;
    searchResults: boolean;
  }>(
    [SHOULD_ANIMATE_FIRST_IMAGE_GALLERY],
    () => ({
      categoryPage: false,
      brandPage: false,
      wishlist: false,
      searchResults: false,
    }),
    {
      disabled: true,
    },
  );

  const shouldShowCategoryPage =
    (isCategoryPage || isPremiumPage) && !data?.categoryPage;
  const shouldShowBrandPage = isBrandPage && !data?.brandPage;
  const shouldShowWishlist = isWishlist && !data?.wishlist;
  const shouldShowSRP = isSRP && !data?.searchResults;

  const initialMount = useRef(true);

  const [shouldShow, setShouldShow] = useState(
    shouldShowBrandPage ||
      shouldShowCategoryPage ||
      shouldShowWishlist ||
      shouldShowSRP,
  );

  // eslint-disable-next-line local-rules/prefer-use-history -- We need it in this case because otherwise it would show e.g on every category
  useEffect(() => {
    if (!initialMount.current) {
      startTransition(() => {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setShouldShow(false);
      });
    } else {
      initialMount.current = false;
    }
  }, [pathname]);

  useEffect(() => {
    if (shouldShow) {
      startTransition(() => {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        updateCache({
          categoryPage:
            (isCategoryPage || isPremiumPage) && !data?.categoryPage,
          brandPage: isBrandPage && !data?.brandPage,
          wishlist: isWishlist && !data?.wishlist,
          searchResults: isSRP && !data?.searchResults,
        });
      });
    }
  }, [isCategoryPage, isWishlist, isBrandPage, isSRP, updateCache, shouldShow]);

  return {
    shouldAnimateFirstImageGallery: shouldShow,
  };
};
